import {
  Lit3formBlock,
  html,
  unsafeHTML,
  ifDefined,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import {
  analyticsLabelFromString,
  adobeCleanString,
  handleAdobeNavEvent,
} from '../../../helpers/adobe-analytics';

export class Edward extends Lit3formBlock {
  static get characterName() {
    return 'edward';
  }

  static get characterProperties() {
    return {
      header: { type: String },
      description: { type: String },
      cta: { type: String },
      _dynamicImageUrl: { type: String },
      _analyticsName: { type: String },
    };
  }

  static get characterStyles() {
    return [...styles];
  }

  constructor() {
    super();
    this._dynamicImageUrl = this.dynamicUrl;
    if (this.hasMobileImage) {
      window.addEventListener('useMobileEvent', e => {
        const useMobile = e.detail?.useMobile;
        this._dynamicImageUrl = useMobile ? this.mobileImageUrl : this.imageUrl;
        this.requestUpdate();
      });
    }
    this.setupAnalyticsData();

    // Temporary workaround. This is just a safety need and will not be needed
    // when we are no longer pushing images to this cookie.
    localStorage.removeItem('requestInfoData');
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div class="block-grid edward-layout">
          <div class="image">
            <a
              href=${this.ctaUrl}
              @click="${event => {
                event.preventDefault();
                handleAdobeNavEvent({
                  navString: this._analyticsName,
                  destinationUrl: this.content?.cta?.url,
                  event,
                });
              }}"
            >
              <img src=${this._dynamicImageUrl} />
            </a>
            ${this.captionHTML}
          </div>
          ${this.headerHTML} ${this.descriptionHTML} ${this.ctaHTML}
          ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get headerHTML() {
    return this.headerText
      ? html`
          <h1 class="header">${unsafeHTML(this.headerText)}</h1>
        `
      : ``;
  }

  get descriptionHTML() {
    return this.descriptionText
      ? html`
          <p class="description">${unsafeHTML(this.descriptionText)}</p>
        `
      : ``;
  }

  get ctaHTML() {
    return this.hasCta
      ? html`
          <cta-button
            class="cta-btn"
            cta-data=${this.ctaInfo}
            cta-type="primary"
          ></cta-button>
        `
      : ``;
  }

  get ctaUrl() {
    return this.hasCta && this.content.cta.url !== '#contact-rep'
      ? this.content.cta.url
      : undefined;
  }

  get ctaInfo() {
    const ctaData = this.content.cta || {};
    ctaData.analyticsName = this._analyticsName;
    return JSON.stringify(ctaData);
  }

  get hasCta() {
    return this.content.cta && !!this.content.cta.text;
  }

  get headerText() {
    return this.withLineBreaks(this.content.header) || '';
  }

  get descriptionText() {
    return this.withLineBreaks(this.content.description) || '';
  }

  get dynamicUrl() {
    return this._useMobileLayout && this.hasMobileImage
      ? this.mobileImageUrl
      : this.imageUrl;
  }

  get imageUrl() {
    return this.imagebossUrl(this.content.image.src, null, 750);
  }

  get hasMobileImage() {
    return !!this.content.image.small_screen_image;
  }

  get mobileImageUrl() {
    return this.imagebossUrl(this.content.image.small_screen_image, 600, null);
  }

  get captionHTML() {
    return !!this.imageCaption
      ? html`
          <div class="image-caption">
            <span>
              <p>${unsafeHTML(this.imageCaption)}</p>
            </span>
            <span>
              <download-button
                downloadData=${JSON.stringify({
                  file_format: 'JPG',
                  file_name: analyticsLabelFromString(this.imageCaption),
                  link_url: this.imageUrl,
                  link_type: 'IMAGE',
                  placement: analyticsLabelFromString(this.headerText),
                })}
              >
              </download-button>
            </span>
          </div>
        `
      : html``;
  }

  get imageCaption() {
    return this.content.caption
      ? this.withLineBreaks(this.content.caption)
      : '';
  }

  setupAnalyticsData() {
    const ctaData = this.content.cta || {};
    const cleanHeader = adobeCleanString(this.headerText);
    const cleanCtaText = adobeCleanString(ctaData.text);
    this._analyticsName = `NAV|Hero ${cleanHeader}|${cleanCtaText}`;
  }
}
Edward.defineElement();
